/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Performance App
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file PerformanceApp.tsx
 * @version 1.0.0
 * @description Dashboard principal de métricas de WhatsApp en tiempo real
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Métricas en tiempo real (enviado/recibido/leído)
 * - Gráficos de tendencias con SVG nativo
 * - Tasa de respuesta con gauge circular
 * - Lista de conversaciones filtrable
 * - Export de reportes (CSV/PDF)
 * - Auto-refresh cada 30 segundos
 * - Comparativas con periodo anterior
 * - Filtros por rango de tiempo
 * - Manejo robusto de errores
 * - Responsive design
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, ButtonGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faRefresh, 
  faExclamationTriangle,
  faClock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getHatateToken } from '../helpers/utils';

// Types
import {
  PerformanceAppProps,
  PerformanceAppState,
  PerformanceMetrics,
  TrendData,
  ConversationItem,
  TimeRange,
  FilterOptions,
  DEFAULT_FILTERS,
  StatsAPIResponse,
  ConversationsAPIResponse,
  MessageDirection,
  MessageStatus
} from '../types/performance_types';

// Components
import MetricsOverview from '../components/MetricsOverview';
import MessageTrendsChart from '../components/MessageTrendsChart';
import ResponseRateGauge from '../components/ResponseRateGauge';
import ConversationsList from '../components/ConversationsList';
import ExportReportButton from '../components/ExportReportButton';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';
const AUTO_REFRESH_INTERVAL = 30000; // 30 segundos
const CACHE_KEY_PREFIX = 'wasp_performance_';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutos

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene el token de autenticación usando el helper de Hatate
 */
const getAuthToken = (): string | null => {
  try {
    const token = getHatateToken();
    return token || null;
  } catch (error) {
    console.error('[PerformanceApp] Error getting auth token:', error);
    return null;
  }
};

/**
 * Guarda datos en cache de localStorage
 */
const setCacheData = (key: string, data: any): void => {
  try {
    const cacheItem = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(cacheItem));
  } catch (error) {
    console.error('[PerformanceApp] Error setting cache:', error);
  }
};

/**
 * Obtiene datos del cache si no han expirado
 */
const getCacheData = (key: string): any | null => {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
    if (!cached) return null;

    const cacheItem = JSON.parse(cached);
    const isExpired = Date.now() - cacheItem.timestamp > CACHE_EXPIRY;

    if (isExpired) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
      return null;
    }

    return cacheItem.data;
  } catch (error) {
    console.error('[PerformanceApp] Error getting cache:', error);
    return null;
  }
};

/**
 * Calcula métricas desde datos de conversaciones
 */
const calculateMetricsFromConversations = (
  conversations: any[],
  timeRange: TimeRange
): PerformanceMetrics => {
  const now = Date.now();
  let cutoffTime = now;

  // Calcular tiempo de corte según rango
  switch (timeRange) {
    case TimeRange.LAST_24H:
      cutoffTime = now - (24 * 60 * 60 * 1000);
      break;
    case TimeRange.LAST_7D:
      cutoffTime = now - (7 * 24 * 60 * 60 * 1000);
      break;
    case TimeRange.LAST_30D:
      cutoffTime = now - (30 * 24 * 60 * 60 * 1000);
      break;
  }

  let totalSent = 0;
  let totalReceived = 0;
  let delivered = 0;
  let read = 0;
  let failed = 0;
  let pending = 0;
  let responseCount = 0;
  let totalResponseTime = 0;

  // Procesar conversaciones
  conversations.forEach(conv => {
    if (!conv.messages) return;

    conv.messages.forEach((msg: any) => {
      const msgTime = new Date(msg.timestamp).getTime();
      if (msgTime < cutoffTime) return;

      // Contar por dirección
      if (msg.direction === 'outgoing') {
        totalSent++;
      } else {
        totalReceived++;
      }

      // Contar por estado
      switch (msg.status) {
        case 'delivered':
          delivered++;
          break;
        case 'read':
          read++;
          break;
        case 'failed':
          failed++;
          break;
        case 'pending':
          pending++;
          break;
      }
    });
  });

  // Calcular tasas
  const deliveryRate = totalSent > 0 ? (delivered / totalSent) * 100 : 0;
  const readRate = delivered > 0 ? (read / delivered) * 100 : 0;
  const responseRate = totalSent > 0 ? (totalReceived / totalSent) * 100 : 0;
  const avgResponseTime = responseCount > 0 ? totalResponseTime / responseCount : 0;

  return {
    totalSent,
    totalReceived,
    delivered,
    read,
    failed,
    pending,
    deliveryRate: Math.round(deliveryRate * 10) / 10,
    readRate: Math.round(readRate * 10) / 10,
    responseRate: Math.round(responseRate * 10) / 10,
    avgResponseTime: Math.round(avgResponseTime),
    activeConversations: conversations.filter(c => c.status === 'active').length,
    totalConversations: conversations.length,
    timeRange,
    lastUpdated: new Date()
  };
};

/**
 * Genera datos de tendencias desde conversaciones
 */
const generateTrendData = (
  conversations: any[],
  timeRange: TimeRange
): TrendData[] => {
  const now = Date.now();
  const data: TrendData[] = [];
  
  let points = 24; // Por defecto 24 puntos para 24h
  let interval = 60 * 60 * 1000; // 1 hora

  switch (timeRange) {
    case TimeRange.LAST_24H:
      points = 24;
      interval = 60 * 60 * 1000; // 1 hora
      break;
    case TimeRange.LAST_7D:
      points = 7;
      interval = 24 * 60 * 60 * 1000; // 1 día
      break;
    case TimeRange.LAST_30D:
      points = 30;
      interval = 24 * 60 * 60 * 1000; // 1 día
      break;
  }

  // Generar puntos de datos
  for (let i = points - 1; i >= 0; i--) {
    const timestamp = new Date(now - (i * interval));
    const nextTimestamp = new Date(now - ((i - 1) * interval));

    let sent = 0;
    let received = 0;
    let delivered = 0;
    let read = 0;

    // Contar mensajes en este intervalo
    conversations.forEach(conv => {
      if (!conv.messages) return;

      conv.messages.forEach((msg: any) => {
        const msgTime = new Date(msg.timestamp).getTime();
        if (msgTime >= timestamp.getTime() && msgTime < nextTimestamp.getTime()) {
          if (msg.direction === 'outgoing') {
            sent++;
            if (msg.status === 'delivered') delivered++;
            if (msg.status === 'read') read++;
          } else {
            received++;
          }
        }
      });
    });

    // Formato de label según rango
    let label = '';
    if (timeRange === TimeRange.LAST_24H) {
      label = timestamp.getHours().toString().padStart(2, '0') + ':00';
    } else {
      label = `${timestamp.getDate()}/${timestamp.getMonth() + 1}`;
    }

    data.push({
      label,
      timestamp,
      value: sent + received,
      values: {
        sent,
        received,
        delivered,
        read
      }
    });
  }

  return data;
};

/**
 * Transforma datos de API a ConversationItem
 */
const transformConversations = (apiConversations: any[]): ConversationItem[] => {
  return apiConversations.map(conv => ({
    id: conv.id || conv._id || String(Math.random()),
    contactName: conv.contactName || conv.name || 'Sin nombre',
    phoneNumber: conv.phoneNumber || conv.phone || conv.from || 'Desconocido',
    lastMessage: conv.lastMessage?.body || conv.lastMessageText || '',
    lastMessageTime: new Date(conv.lastMessage?.timestamp || conv.lastMessageTime || Date.now()),
    unreadCount: conv.unreadCount || 0,
    status: conv.status || 'active',
    messageCount: conv.messageCount || (conv.messages ? conv.messages.length : 0),
    lastMessageDirection: conv.lastMessage?.direction || 'incoming',
    avatarUrl: conv.avatarUrl,
    tags: conv.tags || [],
    assignedTo: conv.assignedTo
  }));
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════

const PerformanceApp: React.FC<PerformanceAppProps> = ({
  compact = false,
  onFilterChange,
  onExport,
  height = 'auto'
}) => {
  
  // ─────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────

  const [state, setState] = useState<PerformanceAppState>({
    metrics: null,
    trends: [],
    conversations: [],
    filters: DEFAULT_FILTERS,
    loading: true,
    error: null,
    lastUpdated: null
  });

  const [refreshing, setRefreshing] = useState<boolean>(false);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ─────────────────────────────────────────────────────────────
  // API CALLS
  // ─────────────────────────────────────────────────────────────

  /**
   * Fetch datos de estadísticas desde API
   */
  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Intentar cache primero
      const cached = getCacheData('stats');
      if (cached && !refreshing) {
        console.log('[PerformanceApp] Using cached stats');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/stats`, {
        method: 'GET',
        headers: {
          'x-api-key': API_KEY,
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Guardar en cache
      setCacheData('stats', result);

      console.log('[PerformanceApp] Stats fetched successfully');

    } catch (error: any) {
      console.error('[PerformanceApp] Error fetching stats:', error);
      throw error;
    }
  }, [refreshing]);

  /**
   * Fetch conversaciones desde API
   */
  const fetchConversations = useCallback(async (): Promise<any[]> => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Intentar cache primero
      const cached = getCacheData('conversations');
      if (cached && !refreshing) {
        console.log('[PerformanceApp] Using cached conversations');
        return cached;
      }

      const response = await fetch(`${API_BASE_URL}/conversations`, {
        method: 'GET',
        headers: {
          'x-api-key': API_KEY,
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const conversations = result.data?.conversations || result.conversations || [];

      // Guardar en cache
      setCacheData('conversations', conversations);

      console.log('[PerformanceApp] Conversations fetched:', conversations.length);

      return conversations;

    } catch (error: any) {
      console.error('[PerformanceApp] Error fetching conversations:', error);
      throw error;
    }
  }, [refreshing]);

  /**
   * Carga todos los datos
   */
  const loadData = useCallback(async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Fetch conversaciones (la fuente principal de datos)
      const rawConversations = await fetchConversations();

      // Calcular métricas desde conversaciones
      const metrics = calculateMetricsFromConversations(
        rawConversations,
        state.filters.timeRange
      );

      // Generar datos de tendencias
      const trends = generateTrendData(
        rawConversations,
        state.filters.timeRange
      );

      // Transformar conversaciones
      const conversations = transformConversations(rawConversations);

      // Actualizar estado
      setState(prev => ({
        ...prev,
        metrics,
        trends,
        conversations,
        loading: false,
        error: null,
        lastUpdated: new Date()
      }));

      console.log('[PerformanceApp] Data loaded successfully');

    } catch (error: any) {
      console.error('[PerformanceApp] Error loading data:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Error al cargar datos'
      }));
    } finally {
      setRefreshing(false);
    }
  }, [state.filters.timeRange, fetchConversations]);

  // ─────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────

  /**
   * Maneja el cambio de rango de tiempo
   */
  const handleTimeRangeChange = useCallback((range: TimeRange) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        timeRange: range
      }
    }));

    if (onFilterChange) {
      onFilterChange({
        ...state.filters,
        timeRange: range
      });
    }
  }, [state.filters, onFilterChange]);

  /**
   * Maneja el refresh manual
   */
  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    
    // Limpiar cache
    localStorage.removeItem(`${CACHE_KEY_PREFIX}stats`);
    localStorage.removeItem(`${CACHE_KEY_PREFIX}conversations`);
    
    loadData();
  }, [loadData]);

  /**
   * Maneja cambios en filtros
   */
  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    setState(prev => ({
      ...prev,
      filters: newFilters
    }));

    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  }, [onFilterChange]);

  // ─────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────

  /**
   * Carga inicial de datos
   */
  useEffect(() => {
    loadData();
  }, []);

  /**
   * Recarga cuando cambia el rango de tiempo
   */
  useEffect(() => {
    if (state.metrics) {
      loadData();
    }
  }, [state.filters.timeRange]);

  /**
   * Auto-refresh cada 30 segundos
   */
  useEffect(() => {
    // Limpiar timer anterior
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
    }

    // Crear nuevo timer
    autoRefreshTimerRef.current = setInterval(() => {
      console.log('[PerformanceApp] Auto-refresh triggered');
      setRefreshing(true);
      loadData();
    }, AUTO_REFRESH_INTERVAL);

    // Cleanup
    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [loadData]);

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <Container fluid style={{ height, padding: compact ? '1rem' : '1.5rem' }}>
      
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-1">
                <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: '#0d6efd' }} />
                Performance Metrics
              </h3>
              {state.lastUpdated && (
                <small className="text-muted">
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  Última actualización: {state.lastUpdated.toLocaleTimeString('es-CL')}
                </small>
              )}
            </div>

            <div className="d-flex gap-2">
              {/* Selector de rango de tiempo */}
              <ButtonGroup>
                <Button
                  variant={state.filters.timeRange === TimeRange.LAST_24H ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleTimeRangeChange(TimeRange.LAST_24H)}
                >
                  24h
                </Button>
                <Button
                  variant={state.filters.timeRange === TimeRange.LAST_7D ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleTimeRangeChange(TimeRange.LAST_7D)}
                >
                  7d
                </Button>
                <Button
                  variant={state.filters.timeRange === TimeRange.LAST_30D ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleTimeRangeChange(TimeRange.LAST_30D)}
                >
                  30d
                </Button>
              </ButtonGroup>

              {/* Botón de refresh */}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <FontAwesomeIcon 
                  icon={faRefresh} 
                  spin={refreshing}
                  className="me-1"
                />
                {refreshing ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {state.error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setState(prev => ({ ...prev, error: null }))}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {state.error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Loading State */}
      {state.loading && !state.metrics && (
        <Row>
          <Col className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Cargando métricas...</p>
          </Col>
        </Row>
      )}

      {/* Content */}
      {!state.loading && state.metrics && (
        <>
          {/* MetricsOverview */}
          <Row className="mb-4">
            <Col>
              <MetricsOverview
                metrics={state.metrics}
                comparison={undefined} // TODO: Implementar comparativas
                loading={refreshing}
                compact={compact}
              />
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="mb-4">
            <Col lg={8}>
              <MessageTrendsChart
                data={state.trends}
                timeRange={state.filters.timeRange}
                onTimeRangeChange={handleTimeRangeChange}
                height={350}
                showLegend={true}
                loading={refreshing}
              />
            </Col>
            <Col lg={4}>
              <ResponseRateGauge
                value={state.metrics.responseRate}
                target={80}
                size={280}
                animated={true}
                comparison={undefined} // TODO: Implementar comparativas
              />
            </Col>
          </Row>

          {/* Conversations List */}
          <Row className="mb-4">
            <Col>
              <ConversationsList
                conversations={state.conversations}
                total={state.conversations.length}
                page={state.filters.page || 1}
                pageSize={state.filters.pageSize || 20}
                onPageChange={(newPage) => handleFilterChange({ ...state.filters, page: newPage })}
                onSelectConversation={(conv) => console.log('Selected conversation:', conv)}
                filters={state.filters}
                onFilterChange={handleFilterChange}
                loading={refreshing}
              />
            </Col>
          </Row>

          {/* Export Button (Floating) */}
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            zIndex: 1000
          }}>
            <ExportReportButton
              metrics={state.metrics}
              conversations={state.conversations}
              onExport={(config) => console.log('Export config:', config)}
              exporting={false}
              showAdvancedOptions={true}
            />
          </div>
        </>
      )}

    </Container>
  );
};

export default PerformanceApp;